import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  Input,
  LOCALE_ID,
  signal,
} from "@angular/core";

import { ColDef } from "ag-grid-enterprise";
import {
  LinkCellRendererComponent,
  UnitOfMeasurementCellRendererComponent,
} from "src/app/shared/cell-renderers";
import { ProcessInputOutputTypeEnum, TableEnum } from "src/app/shared/enums";
import {
  IBaseUnit,
  IItemExtended,
  IMaterial,
  IProcessItem,
  IProductExtended,
} from "src/app/shared/interfaces";
import { AuthenticationService, ItemsService } from "src/app/shared/services";
import { ColumnUtils, CommonUtils } from "src/app/shared/utils";

import { TextConstants } from "@shared/constants";
import { RouterService } from "@shared/services/router.service";

@Component({
  standalone: false,
  selector: "app-process-selected-items-table",
  templateUrl: "./process-selected-items-table.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProcessSelectedItemsTableComponent implements AfterViewInit {
  @Input()
  public type: string;

  @Input()
  public items: IProcessItem[] = [];

  @Input()
  public allMaterials: IMaterial[] = [];

  @Input()
  public allProducts: IProductExtended[] = [];

  public allUnitOfMeasurements = input<IBaseUnit[]>([]);

  @Input()
  public areButtonsEnabled = true;

  @Input()
  public isSearchEnabled = true;

  @Input()
  public isPaginatorEnabled = true;

  public readonly table = TableEnum.PROCESS_SELECTED_ITEMS;

  public readonly processInputOutputTypeEnum = ProcessInputOutputTypeEnum;

  @Input()
  public isSaveTableState = false;

  @Input()
  public columns: string[] = [
    "itemId",
    "productName",
    "materialData",
    "initialQuantity",
    "selectedQuantity",
  ];

  public isLoading = signal(true);

  public rowData: any[] = [];

  public columnDefs = signal<ColDef[]>([]);

  public readonly isRegularUser = this.authenticationService.isRegularUser();

  @Input()
  public isInboundShared = false;

  @Input()
  public inboundSharedSenderOrgId: string = null;

  @Input()
  public inboundItems: IItemExtended[] = [];

  private readonly locale: string = inject(LOCALE_ID);

  constructor(
    private itemService: ItemsService,
    private router: RouterService,
    private authenticationService: AuthenticationService,
  ) {}

  public async ngAfterViewInit() {
    this.setColumnDefs();
    await this.getAll();
  }

  public onViewDetails = async (row: any): Promise<void> => {
    const link = this.isInboundShared
      ? this.router.getSharedItemLink(row.id, false, {
          organisationId: this.inboundSharedSenderOrgId,
        })
      : this.router.getItemLink(row.id);

    await this.router.navigate(link);
  };

  private setColumnDefs = (): void => {
    const columnDefs: ColDef[] = [
      {
        headerName: $localize`:@@process-selected-items-table.itemid:Item ID`,
        field: "itemId",
        lockVisible: true,
        cellRenderer: LinkCellRendererComponent,
        cellRendererParams: {
          linkRouteFn: this.isInboundShared
            ? (id) => {
                return this.router.getSharedItemLink(id, false, {
                  organisationId: this.inboundSharedSenderOrgId,
                });
              }
            : this.router.getItemLink,
          linkRouteIdParam: "id",
        },
      },
      {
        headerName: TextConstants.INITIAL_QTY,
        field: "initialQuantity",
        cellRenderer: UnitOfMeasurementCellRendererComponent,
        cellRendererParams: {
          precisionParam: "precision",
          symbolParam: "symbol",
          formattedQuantityParam: "initialQuantityWithDefaultUnit",
        },
      },
    ];

    if (this.type === this.processInputOutputTypeEnum.INPUT) {
      columnDefs.push({
        headerName: TextConstants.SELECTED_QTY,
        field: "selectedQuantity",
        cellRenderer: UnitOfMeasurementCellRendererComponent,
        cellRendererParams: {
          precisionParam: "precision",
          symbolParam: "symbol",
          formattedQuantityParam: "selectedQuantityWithDefaultUnit",
        },
      });
    }
    columnDefs.push(
      {
        headerName: TextConstants.PRODUCT,
        field: "productName",
        cellRenderer: LinkCellRendererComponent,
        cellRendererParams: {
          linkRouteFn: this.isInboundShared
            ? (id) =>
                this.router.getSharedProductLink(id, false, {
                  organisationId: this.inboundSharedSenderOrgId,
                })
            : this.router.getProductLink,
          linkRouteIdParam: "productId",
        },
      },
      ColumnUtils.chips(TextConstants.MATERIALS, "materialData", {
        textParam: "name",
        linkRouteIdParam: "id",
        linkRouteFn: (id) =>
          this.isInboundShared
            ? this.router.getSharedMaterialLink(id, false, {
                organisationId: this.inboundSharedSenderOrgId,
              })
            : this.router.getMaterialLink(id, false),
      }),
    );

    this.columnDefs.set(CommonUtils.getVisibleColumnDefs(columnDefs, this.columns));
  };

  private getParsedRowData = async (selectedProcessItems: IProcessItem[]): Promise<any[]> => {
    if (!selectedProcessItems || selectedProcessItems.length === 0) {
      return [];
    }

    // Collect all item IDs
    const itemIds = selectedProcessItems.map((processItem) =>
      CommonUtils.getUriId(processItem.item),
    );

    // Get all items at once using GraphQL
    const items = await this.itemService.getByIdsGraphQL(itemIds);
    const result = [];

    for (const processItem of selectedProcessItems) {
      const itemId = CommonUtils.getUriId(processItem.item);
      const item = items.find((i) => i.id === itemId);

      if (!item) {
        continue;
      }

      const product = item.product;
      const defaultUnit = product?.defaultCustomUnit;
      const unitOfMeasurement = product?.unitOfMeasurement;
      let initialQuantityWithDefaultUnit: string;
      let selectedQuantityWithDefaultUnit: string;

      if (this.isRegularUser && defaultUnit) {
        const productDefaultUnit = defaultUnit;

        initialQuantityWithDefaultUnit = CommonUtils.formatQuantityWithDefaultUnit(
          this.locale,
          item.initialQuantity,
          productDefaultUnit,
          unitOfMeasurement,
        );
        selectedQuantityWithDefaultUnit = CommonUtils.formatQuantityWithDefaultUnit(
          this.locale,
          processItem.selectedQuantity,
          productDefaultUnit,
          unitOfMeasurement,
        );
      }
      const dataElement: any = {
        id: itemId,
        itemId: item.itemId,
        productId: product?.id,
        productName: product?.name,
        materialData: item.materials,
        initialQuantity: item.initialQuantity,
        precision: unitOfMeasurement?.precision,
        symbol: unitOfMeasurement?.symbol,
        initialQuantityWithDefaultUnit,
      };

      if (this.type === this.processInputOutputTypeEnum.INPUT) {
        dataElement.selectedQuantity = processItem.selectedQuantity;
        if (selectedQuantityWithDefaultUnit) {
          dataElement.selectedQuantityWithDefaultUnit = selectedQuantityWithDefaultUnit;
        }
      }
      result.push(dataElement);
    }

    return result;
  };

  getParsedInboundData() {
    const result = [];

    for (const processItem of this.items) {
      const item = this.inboundItems.find((i) => i.id === CommonUtils.getUriId(processItem.item));

      if (!item) {
        continue;
      }
      const product = item?.product as any as IProductExtended;
      const defaultUnit = product?.defaultCustomUnit;
      const unitOfMeasurement = product?.unitOfMeasurement;
      let initialQuantityWithDefaultUnit: string;
      let selectedQuantityWithDefaultUnit: string;

      if (this.isRegularUser && defaultUnit) {
        const productDefaultUnit = defaultUnit;

        initialQuantityWithDefaultUnit = CommonUtils.formatQuantityWithDefaultUnit(
          this.locale,
          item.initialQuantity,
          productDefaultUnit,
          unitOfMeasurement,
        );
        selectedQuantityWithDefaultUnit = CommonUtils.formatQuantityWithDefaultUnit(
          this.locale,
          processItem.selectedQuantity,
          productDefaultUnit,
          unitOfMeasurement,
        );
      }
      const dataElement: any = {
        id: item?.id,
        itemId: item?.itemId,
        productId: product?.id,
        productName: product?.name,
        materialData: item?.materials,
        initialQuantity: item?.initialQuantity,
        precision: unitOfMeasurement?.precision,
        symbol: unitOfMeasurement?.symbol,
        initialQuantityWithDefaultUnit,
      };

      if (this.type === this.processInputOutputTypeEnum.INPUT) {
        dataElement.selectedQuantity = processItem.selectedQuantity;
        if (selectedQuantityWithDefaultUnit) {
          dataElement.selectedQuantityWithDefaultUnit = selectedQuantityWithDefaultUnit;
        }
      }
      result.push(dataElement);
    }

    return result;
  }

  private getAll = async (): Promise<void> => {
    this.isLoading.set(true);
    this.rowData = this.isInboundShared
      ? this.getParsedInboundData()
      : await this.getParsedRowData([...this.items]);
    this.isLoading.set(false);
  };
}
